import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stack,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetcher, useQ } from '@olagg/api-hooks';
import { AdminPushNotificationEndpoints } from '@olagg/api-hooks/endpoints/adminEndpoints';
import { AdminSegmentEndpoints } from '@olagg/api-hooks';
import { Segment } from '@olagg/db-types';
import { usePushNotification } from './usePushNotification';

export const EditPushNotification = () => {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const { pushNotification, isLoading } = usePushNotification(uid!);

  const [targetType, setTargetType] = useState<'segment' | 'user'>('segment');
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    image: '',
    link: '',
    notification_segment_id: '',
    user_id: '',
    scheduled_at: '',
    status: 'draft' as 'draft' | 'scheduled',
  });

  const { data: segmentsData } = useQ<{ segments: Segment[] }>(
    AdminSegmentEndpoints.all({ page: 1, limit: 100, name: '' }),
    { revalidateOnFocus: false }
  );

  const segments = segmentsData?.segments || [];

  useEffect(() => {
    if (pushNotification) {
      setFormData({
        title: pushNotification.title,
        body: pushNotification.body,
        image: pushNotification.image || '',
        link: pushNotification.link || '',
        notification_segment_id: pushNotification.notification_segment?.id?.toString() || '',
        user_id: pushNotification.user?.id?.toString() || '',
        scheduled_at: pushNotification.scheduled_at
          ? new Date(pushNotification.scheduled_at).toISOString().slice(0, 16)
          : '',
        status: pushNotification.status as 'draft' | 'scheduled',
      });
      setTargetType(pushNotification.target_type as 'segment' | 'user');
    }
  }, [pushNotification]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pushNotification?.can_edit) {
      toast({
        title: 'Error',
        description: 'Esta notificación no se puede editar',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const payload: any = {
      title: formData.title,
      body: formData.body,
      image: formData.image || undefined,
      link: formData.link || undefined,
      status: formData.status,
    };

    if (targetType === 'segment') {
      payload.notification_segment_id = Number(formData.notification_segment_id);
      payload.user_id = null;
    } else {
      payload.user_id = Number(formData.user_id);
      payload.notification_segment_id = null;
    }

    if (formData.status === 'scheduled' && formData.scheduled_at) {
      payload.scheduled_at = new Date(formData.scheduled_at).toISOString();
    }

    try {
      await fetcher(AdminPushNotificationEndpoints.update(uid!, payload));
      toast({
        title: 'Éxito',
        description: 'Push notification actualizada correctamente',
        status: 'success',
        duration: 3000,
      });
      navigate('/push-notifications');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Error al actualizar la notificación',
        status: 'error',
        duration: 3000,
      });
    }
  };

  if (isLoading) {
    return (
      <Center w={'full'} h={'50vh'}>
        <Spinner size={'xl'} color={'purple.500'} />
      </Center>
    );
  }

  if (!pushNotification) {
    return (
      <Center w={'full'} h={'50vh'}>
        <Heading color={'white'}>Notificación no encontrada</Heading>
      </Center>
    );
  }

  if (!pushNotification.can_edit) {
    return (
      <Center w={'full'} h={'50vh'}>
        <VStack>
          <Heading color={'white'}>Esta notificación no se puede editar</Heading>
          <Button onClick={() => navigate('/push-notifications')} colorScheme={'purple'}>
            Volver
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Center w={'full'} p={4}>
      <VStack color={'white'} w={'full'} maxW={'800px'} spacing={6}>
        <Heading my={2}>Editar Push Notification</Heading>

        <Box w={'full'} bg={'gray.800'} p={6} borderRadius={'md'}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Título</FormLabel>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Título de la notificación"
                  bg={'gray.700'}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Mensaje</FormLabel>
                <Textarea
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="Cuerpo del mensaje"
                  bg={'gray.700'}
                  rows={4}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Imagen (URL)</FormLabel>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://example.com/image.png"
                  bg={'gray.700'}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Link (URL)</FormLabel>
                <Input
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  placeholder="https://olagg.io/evento/123"
                  bg={'gray.700'}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Enviar a:</FormLabel>
                <RadioGroup value={targetType} onChange={(val) => setTargetType(val as 'segment' | 'user')}>
                  <Stack direction="row">
                    <Radio value="segment">Segmento</Radio>
                    <Radio value="user">Usuario</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {targetType === 'segment' && (
                <FormControl isRequired>
                  <FormLabel>Segmento</FormLabel>
                  <Select
                    value={formData.notification_segment_id}
                    onChange={(e) => setFormData({ ...formData, notification_segment_id: e.target.value })}
                    placeholder="Seleccionar segmento"
                    bg={'gray.700'}
                  >
                    {segments.map((segment) => (
                      <option key={segment.id} value={segment.id}>
                        {segment.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}

              {targetType === 'user' && (
                <FormControl isRequired>
                  <FormLabel>User ID</FormLabel>
                  <Input
                    type="number"
                    value={formData.user_id}
                    onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                    placeholder="123"
                    bg={'gray.700'}
                  />
                </FormControl>
              )}

              <FormControl isRequired>
                <FormLabel>Estado</FormLabel>
                <RadioGroup
                  value={formData.status}
                  onChange={(val) => setFormData({ ...formData, status: val as 'draft' | 'scheduled' })}
                >
                  <Stack direction="row">
                    <Radio value="draft">Borrador</Radio>
                    <Radio value="scheduled">Programar</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {formData.status === 'scheduled' && (
                <FormControl isRequired>
                  <FormLabel>Fecha y hora de envío</FormLabel>
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_at}
                    onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                    bg={'gray.700'}
                  />
                </FormControl>
              )}

              <Stack direction="row" spacing={4} w={'full'} justifyContent={'flex-end'} mt={4}>
                <Button variant={'outline'} onClick={() => navigate('/push-notifications')}>
                  Cancelar
                </Button>
                <Button type="submit" colorScheme={'purple'}>
                  Guardar
                </Button>
              </Stack>
            </VStack>
          </form>
        </Box>
      </VStack>
    </Center>
  );
};
